import { Component, OnInit } from '@angular/core';
import { ProductionService } from '@app/shared/services/production.service';
import { ChangeDetectorRef } from '@angular/core';
import { RepeatOrderService } from '@app/shared/services/repeat-order.service';
import { OrderDataService } from '@app/shared/services/order-data.service';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import * as XLSX from 'xlsx';  // Import XLSX for Excel export

interface Production {
  _id: string;
  totalDispatched?: number;
  kgsBal?: number;
  status?: string;
  created: string;  // Ensure `created` field is a string for date comparisons
}
@Component({
  selector: 'app-prod-table',
  templateUrl: './prod-table.component.html',
  styleUrls: ['./prod-table.component.css']
})
export class ProdTableComponent implements OnInit {
  productions: Production[] = [];  // Array to hold the merged production data
  filteredProductions: Production[] = [];  // Filtered productions based on date range
  fromDate: string = '';
  toDate: string = '';
  currentSortColumn: string = '';
  currentSortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private productionService: ProductionService,
    private repeatOrderService: RepeatOrderService,
    private orderDataService: OrderDataService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Fetch data from all services and merge the results
    forkJoin({
      repeatOrders: this.repeatOrderService.getAllRepeatOrder(),
      unsavedOrders: this.orderDataService.getAllUnsavedOrders()
    }).pipe(
      switchMap((data) => {
        // Merge both repeat orders and unsaved orders into a single array
        this.productions = [
          ...(Array.isArray(data.repeatOrders) ? data.repeatOrders : [data.repeatOrders]),
          ...(Array.isArray(data.unsavedOrders) ? data.unsavedOrders : [data.unsavedOrders])
        ];

        this.filteredProductions = [...this.productions];  // Initially show all data
        // Trigger change detection if necessary
        this.cdr.detectChanges();

        return this.loadProductionDetails();
      })
    ).subscribe({
      next: (productionDetails) => {
        productionDetails.forEach((statusData, index) => {
          if (statusData) {
            this.productions[index].totalDispatched = statusData.dispatched;
            this.productions[index].kgsBal = statusData.kgsBal;
            this.productions[index].status = statusData.status;
          }
        });

        this.applyDateFilter();  // Apply any date filter after data is loaded
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching data', err);
      }
    });
  }

  loadProductionDetails() {
    const productionDetailsCalls = this.productions.map((production) =>
      this.productionService.getProductionByproductionId(production._id).pipe(
        catchError((error) => {
          console.error('Error fetching Productions for ID:', production._id, error);
          return of(null);  // Handle the error gracefully but log it
        })
      )
    );
    return forkJoin(productionDetailsCalls);
  }

  applyDateFilter() {
    if (!this.fromDate && !this.toDate) {
      this.filteredProductions = [...this.productions];  // Show all data if no filter is applied
      return;
    }

    const from = this.fromDate ? new Date(this.fromDate) : null;
    const to = this.toDate ? new Date(this.toDate) : null;

    this.filteredProductions = this.productions.filter(production => {
      const productionDate = new Date(production.created);

      // Check if the Productions date is within the range
      const isAfterFromDate = from ? productionDate >= from : true;
      const isBeforeToDate = to ? productionDate <= to : true;

      return isAfterFromDate && isBeforeToDate;
    });
}


  sortTable(column: string) {
    const direction = this.currentSortColumn === column && this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    this.currentSortColumn = column;
    this.currentSortDirection = direction;

    this.filteredProductions.sort((a, b) => {
      const aVal = a[column];
      const bVal = b[column];
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  exportToExcel() {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.filteredProductions);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'productions');
    XLSX.writeFile(wb, 'productions_report.xlsx');
  }
}

